import { Component, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { AppService } from '@shared/services/app.service';
import { AuthStateService } from '@shared/services/auth-state.service';
import { Location } from '@angular/common';
import { SelectorSucursal } from '@shared/components/selector-sucursal/selector-sucursal';
import { SelectorImpresora } from '@shared/components/selector-impresora/selector-impresora';
import { EmpresaService } from '@shared/services/empresa.service';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-panel-header',
  imports: [SelectorSucursal, SelectorImpresora],
  templateUrl: './panel-header.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './panel-header.css',
})
export class PanelHeader {
  private appService = inject(AppService);
  private authState = inject(AuthStateService);
  private location = inject(Location);
  private empresaService = inject(EmpresaService);
  nombreUsuario = this.authState.nombreCompleto;
  emailUsuario = this.authState.email;
  title = this.appService.panel_header_title;
  subtitle = this.appService.panel_header_subtitle;
  showBackButton = this.appService.panel_header_showBackButton;
  icon = this.appService.panel_header_icon;
  mostrarControlesMoviles = signal(false);
  empresa = toSignal(this.empresaService.empresaConfigData$, {
    initialValue: this.empresaService.getEmpresaData(),
  });

  private readonly msPorDia = 24 * 60 * 60 * 1000;

  fechaVencimiento = computed(() => this.parseFecha(this.empresa()?.licencia_vence));
  diasRestantes = computed(() => {
    const fechaVencimiento = this.fechaVencimiento();
    if (!fechaVencimiento) {
      return null;
    }

    const hoy = new Date();
    const inicioHoy = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());
    const inicioVencimiento = new Date(
      fechaVencimiento.getFullYear(),
      fechaVencimiento.getMonth(),
      fechaVencimiento.getDate(),
    );

    return Math.ceil((inicioVencimiento.getTime() - inicioHoy.getTime()) / this.msPorDia);
  });

  mostrarAlertaVencimiento = computed(() => {
    const diasRestantes = this.diasRestantes();
    return diasRestantes !== null && diasRestantes <= 3;
  });

  estadoAlertaVencimiento = computed(() => {
    const diasRestantes = this.diasRestantes();

    if (diasRestantes === null) {
      return '';
    }

    if (diasRestantes < 0) {
      return 'VENCIDA';
    }

    if (diasRestantes === 0) {
      return 'VENCE HOY';
    }

    return 'POR VENCER';
  });

  alertaVencimientoTexto = computed(() => {
    const diasRestantes = this.diasRestantes();
    const fechaVencimiento = this.empresa()?.licencia_vence;

    if (diasRestantes === null || fechaVencimiento === undefined) {
      return '';
    }

    if (diasRestantes < 0) {
      return `Licencia vencida el ${this.formatearFecha(fechaVencimiento)}`;
    }

    if (diasRestantes === 0) {
      return `La licencia vence hoy ${this.formatearFecha(fechaVencimiento)}`;
    }

    return `Se vence pronto en ${diasRestantes} días: ${this.formatearFecha(fechaVencimiento)}`;
  });

  goBack() {
    this.location.back();
  }

  toggleControlesMoviles() {
    this.mostrarControlesMoviles.set(!this.mostrarControlesMoviles());
  }

  private parseFecha(valor?: string | null): Date | null {
    if (!valor) {
      return null;
    }

    const fecha = new Date(valor);
    if (Number.isNaN(fecha.getTime())) {
      return null;
    }

    return fecha;
  }

  private formatearFecha(valor: string): string {
    const fecha = new Date(valor);
    if (Number.isNaN(fecha.getTime())) {
      return valor;
    }

    return fecha.toLocaleDateString('es-PE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }
}
